var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { v4 } from "uuid";
import { getErrorDescription, getTruthyValue } from "@draftkings/sportsbook-common-utils";
import { telemetry } from "../constants";
import { shouldSocketReconnect, createSocketMessage, Tracker, getNavigatorConnectionData, defaultAsyncSocket } from "../helpers";
import { msgPackResponseFormatter, getFormat } from "./helpers";
export class MyBetsSocketConnection {
    constructor(options) {
        this.setAuthToken = (token) => {
            this.token = token;
        };
        this.handleResponse = (query, p, trackMessageType) => {
            if ("result" in p || "data" in p) {
                this.track(trackMessageType, "response");
                this.isPushUpdate = true;
                if (this.lastMessageType !== "unsubscribe" && p.result !== "Unsubscribed") {
                    query.onData({
                        type: this.queryMap[p.id],
                        data: p,
                        id: p.id
                    });
                }
            }
        };
        this.url = options.url;
        this.token = options.token;
        this.asyncSocket = defaultAsyncSocket;
        this.getAsyncSocket = options.getAsyncSocket;
        this.id = "";
        this.lastMessageType = "initialBetsPage";
        this.reconnects = 0;
        this.onTokenNotValid = options.onTokenNotValid;
        this.additionalDelay = options.additionalDelay;
        this.timeoutForReconnect = 0;
        this.isTelemetryEnabled = options.isTelemetryEnabled;
        this.isPushUpdate = false;
        this.isErrorTracked = false;
        this.queryMap = {};
        this.isMsgPack = options.isMsgPack;
        this.subscriptionsValues = ["initialBetsPage", "cashOutOperations"];
        this.messagesMap = {
            initialBetsPage: (request) => {
                this.queryMap[this.id] = "initialBetsPage";
                return createSocketMessage({
                    method: "InitializeBetsPageRequest",
                    id: this.id,
                    params: {
                        betsRequest: request,
                        cashOut: {
                            information: true,
                            pullOperations: true
                        },
                        locale: options.locale
                    },
                }, options.isMsgPack);
            },
            nextBetsPage: (request) => {
                const id = v4();
                this.queryMap[id] = "nextBetsPage";
                return createSocketMessage({
                    method: "BetsRequest",
                    id,
                    params: Object.assign(Object.assign({}, request), { locale: options.locale }),
                }, options.isMsgPack);
            },
            cashOutOperations: (request) => {
                const id = v4();
                this.queryMap[id] = "cashOutOperations";
                return createSocketMessage({
                    method: "CashOutOperationsRequest",
                    id,
                    params: Object.assign({}, request)
                }, options.isMsgPack);
            },
            manualUnsubscribe: (request) => {
                const { id } = request;
                if (id && this.queryMap[id]) {
                    delete this.queryMap[id];
                }
                return createSocketMessage({
                    method: "Unsubscribe",
                    id,
                }, this.isMsgPack);
            },
        };
    }
    deactivate() {
        this.isPushUpdate = false;
        this.lastMessageType = "unsubscribe";
        if (this.asyncSocket.isConnectedSync()) {
            if (Object.keys(this.queryMap).length > 0) {
                for (const key in this.queryMap) {
                    if (this.queryMap.hasOwnProperty(key)) {
                        const type = this.queryMap[key];
                        if (this.subscriptionsValues.includes(type)) {
                            this.asyncSocket.send(createSocketMessage({
                                method: "Unsubscribe",
                                id: key,
                            }, this.isMsgPack));
                        }
                    }
                }
            }
            else {
                this.asyncSocket.send(createSocketMessage({
                    method: "Unsubscribe",
                    id: this.id,
                }, this.isMsgPack));
            }
        }
        this.asyncSocket = defaultAsyncSocket;
        window.clearTimeout(this.timeoutForReconnect);
    }
    subscribe(query, setMyBetsSubscription, onReconnect, onCloseHandler) {
        this.id = v4();
        this.asyncSocket = this.getAsyncSocket({
            url: {
                baseUrl: this.url,
                queryParams: {
                    jwt: this.token,
                    format: getFormat(this.isMsgPack)
                }
            },
            handlers: {
                open: (message) => message,
                error: this.buildErrorHandler(query).bind(this),
                close: this.buildCloseHandler(onReconnect, onCloseHandler).bind(this),
                message: this.buildMessageHandler(query).bind(this),
                requestFormatter: (message) => message,
                responseFormatter: (event) => event
            },
        });
        this.track("socketConnecting", "request");
        this.connectSocket().then(() => {
            this.track("socketConnecting", "response");
        }).catch((e) => {
            this.isErrorTracked = true;
            this.trackError("socketConnecting", e.code, e.status, getErrorDescription(e));
        }).finally(() => {
            setMyBetsSubscription({
                dispose: () => undefined,
                send: this.send.bind(this),
            });
        });
    }
    buildMessageHandler(query) {
        return (event) => {
            if (this.isMsgPack) {
                msgPackResponseFormatter(event).then(response => this.handleMessages(query, response)).catch(undefined);
            }
            else {
                this.handleMessages(query, JSON.parse(event.data));
            }
        };
    }
    handleMessages(query, p) {
        var _a, _b, _c;
        //Ignoring next line due to complex mocking and simulating of behavior, which won't provide useful tests
        /* istanbul ignore next */
        const trackMessageType = (p.id !== this.id || !this.isPushUpdate) && this.queryMap[p.id] ? this.queryMap[p.id] : "pushMessage";
        if ("error" in p) {
            this.trackError(trackMessageType, p.error.code, `${getTruthyValue((_a = p.error.data) === null || _a === void 0 ? void 0 : _a.StatusCode, p.error.status)}, errorCode: ${getTruthyValue((_b = p.error.data) === null || _b === void 0 ? void 0 : _b.ErrorCode, "")}`, getTruthyValue((_c = p.error.data) === null || _c === void 0 ? void 0 : _c.Description, p.error.data));
            this.handleError(query, p);
        }
        this.handleResponse(query, p, trackMessageType);
    }
    buildErrorHandler(query) {
        return (event) => {
            const defaultError = {
                id: this.id,
                error: {
                    code: "unknown",
                    status: "unknown",
                    data: "General Error"
                }
            };
            const error = typeof event.data === "string" ? JSON.parse(event.data) : defaultError;
            //don"t track errors which are already tracked(connetion failed or reconnect failed)
            if (!this.isErrorTracked) {
                this.trackError(this.lastMessageType, error.code, error.status, error.data);
            }
            return this.handleError(query, error);
        };
    }
    handleError(query, error) {
        this.isErrorTracked = false;
        query.onError({
            type: getTruthyValue(this.queryMap[error.id], this.lastMessageType),
            data: error,
            id: error.id
        });
    }
    connectSocket() {
        return __awaiter(this, void 0, void 0, function* () {
            const isSocketConnected = yield this.asyncSocket.isConnected();
            if (!isSocketConnected) {
                yield this.asyncSocket.connect();
            }
        });
    }
    track(type, method) {
        Tracker.getInstance().trackAction(this.isTelemetryEnabled, telemetry.events.myBets, {
            Method: telemetry.network[type][method],
            ClientType: telemetry.clientType,
            MyBetsVersion: telemetry.version,
        });
    }
    trackError(type, code, status, description) {
        Tracker.getInstance().trackEvent(telemetry.events.myBetsError, Object.assign({ Method: telemetry.network[type].exception, ClientType: telemetry.clientType, MyBetsVersion: telemetry.version, Code: code, Status: status, Description: description }, getNavigatorConnectionData()));
    }
    buildCloseHandler(onReconnect, onCloseHandler) {
        return (event) => {
            this.isPushUpdate = false;
            this.track("socketClosed", "request");
            const [decision, delay] = shouldSocketReconnect(event);
            onCloseHandler();
            if (decision) {
                window.clearTimeout(this.timeoutForReconnect);
                this.timeoutForReconnect = window.setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    yield this.onTokenNotValid();
                    this.track("socketReconnecting", "request");
                    try {
                        yield this.asyncSocket.connect({
                            baseUrl: this.url,
                            queryParams: {
                                jwt: this.token,
                                format: getFormat(this.isMsgPack)
                            }
                        });
                        this.track("socketReconnecting", "response");
                    }
                    catch (e) {
                        this.isErrorTracked = true;
                        this.trackError("socketReconnecting", "", "", getErrorDescription(e));
                    }
                    if (this.asyncSocket.isConnectedSync()) {
                        this.reconnects = 0;
                        onReconnect();
                    }
                    else {
                        this.reconnects += 1;
                    }
                }), delay + (this.reconnects * this.additionalDelay));
            }
            return [decision, delay];
        };
    }
    send(message) {
        return this.asyncSocket.isConnected().then((isConnected) => {
            if (isConnected) {
                this.track(message.method, "request");
                const socketMessageHandler = this.messagesMap[message.method];
                if (message) {
                    this.lastMessageType = message.method;
                    this.asyncSocket.send(socketMessageHandler(message.params));
                }
            }
        });
    }
}
//# sourceMappingURL=MyBetsSocketConnection.js.map